import { createTheme, ThemeOptions } from '@mui/material';
import { PinnacleThemes } from './types';

const DEFAULT_FONT_FAMILY = '"Inter", sans-serif';

const PRIMARY_COLOR = '#5840BA';
const SECONDARY_COLOR = '#FFFC00';

const themes = new Map<PinnacleThemes, ThemeOptions>();
themes.set(PinnacleThemes.MUI_DEFAULT, {});

themes.set(PinnacleThemes.BASE_THEME, {
    typography: {
        allVariants: {},
        fontFamily: DEFAULT_FONT_FAMILY,
        h1: { fontSize: '2rem', fontWeight: 300 },
        h2: { fontSize: '1.5rem', fontWeight: 300 },
        h3: { fontSize: '1.25rem', fontWeight: 400 },
        h4: { fontSize: '1.125rem', fontWeight: 400 },
        h5: { fontSize: '1rem', fontWeight: 400 },
        h6: { fontSize: '0.875rem', fontWeight: 500 },
        subtitle1: { fontSize: '0.875rem', fontWeight: 400 },
        subtitle2: { fontSize: '0.75rem', fontWeight: 500 },
        body1: { fontSize: '1rem', fontWeight: 300 },
        body2: { fontSize: '0.875rem', fontWeight: 400 },
        button: { fontSize: '0.875rem', fontWeight: 400 },
        caption: { fontSize: '0.75rem', fontWeight: 400 },
        overline: { fontSize: '0.625rem', fontWeight: 400 },
    },
    palette: {
        primary: {
            main: PRIMARY_COLOR,
            light: 'rgba(0, 51, 103, 0.15)',
        },
        secondary: {
            main: SECONDARY_COLOR,
            light: 'rgba(255, 220, 0, 0.15)',
        },
        common: {
            white: '#ffffff',
            black: '#000000',
        },
        text: {
            primary: '#373737',
            secondary: '#373737',
            disabled: '#777777',
        },
        disabled: {
            icon: '#777777',
            text: '#777777',
            background: '#C8C8C8',
        },
    },
    components: {
        MuiAlert: {
            styleOverrides: {
                standardSuccess: {
                    backgroundColor: '#03B568',
                    color: '#028A4F !important'
                },
                standardError: {
                    backgroundColor: '#FA3D3D',
                },
                standardInfo: {
                    backgroundColor: '#90B0D2',
                },
                standardWarning: {
                    backgroundColor: '#FFA000',
                },
            },
        },

        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    fontWeight: 400
                },
            },
            defaultProps: {
                color: 'primary',
            },
            variants: [
                {
                    props: { variant: 'contained', color: 'primary' },
                    style: {
                        backgroundColor: PRIMARY_COLOR,
                        '&:hover': {
                            backgroundColor: 'rgba(88, 64, 186, 0.9)',
                        },
                        '&.Mui-disabled': {
                            backgroundColor: PRIMARY_COLOR,
                            color: '#fff',
                            opacity: 0.5,
                        },
                    },
                },
                {
                    props: { variant: 'outlined', color: 'primary' },
                    style: {
                        color: PRIMARY_COLOR,
                        border: `1px solid ${PRIMARY_COLOR}`,
                        '&:hover': {
                            textDecoration: 'none',
                            backgroundColor: 'rgba(0, 51, 103, 0.04)',
                            border: `1px solid ${PRIMARY_COLOR}`,
                        },
                        '&.Mui-disabled': {
                            color: PRIMARY_COLOR,
                            border: `1px solid ${PRIMARY_COLOR}`,
                            opacity: 0.5,
                        },
                    },
                },
                {
                    props: { variant: 'text', color: 'primary' },
                    style: {
                        color: PRIMARY_COLOR,
                        '&:hover': {
                            backgroundColor: 'rgba(0, 51, 103, 0.04)',
                        },
                        '&.Mui-disabled': {
                            color: PRIMARY_COLOR,
                            opacity: 0.5,
                        },
                    },
                },
                {
                    props: { variant: 'contained', color: 'secondary' },
                    style: {
                        backgroundColor: SECONDARY_COLOR,
                        color: '#000',
                        '&:hover': {
                            backgroundColor: 'rgba(255, 220, 0, 0.9)',
                        },
                        '&.Mui-disabled': {
                            backgroundColor: PRIMARY_COLOR,
                            color: '#fff',
                            opacity: 0.5,
                        },
                    },
                },
                {
                    props: { variant: 'outlined', color: 'secondary' },
                    style: {
                        color: SECONDARY_COLOR,
                        border: `1px solid ${SECONDARY_COLOR}`,
                        '&:hover': {
                            textDecoration: 'none',
                            backgroundColor: 'rgba(255, 220, 0, 0.04)',
                            border: `1px solid ${SECONDARY_COLOR}`,
                        },
                        '&.Mui-disabled': {
                            color: PRIMARY_COLOR,
                            border: `1px solid ${PRIMARY_COLOR}`,
                            opacity: 0.5,
                        },
                    },
                },
                {
                    props: { variant: 'text', color: 'secondary' },
                    style: {
                        color: SECONDARY_COLOR,
                        '&:hover': {
                            backgroundColor: 'rgba(255, 220, 0, 0.04)',
                        },
                        '&.Mui-disabled': {
                            color: PRIMARY_COLOR,
                            opacity: 0.5,
                        },
                    },
                },
            ],
        },
        MuiIcon: {
            variants: [
                {
                    props: { color: 'primary' },
                    style: { color: PRIMARY_COLOR },
                },
                {
                    props: { color: 'secondary' },
                    style: { color: SECONDARY_COLOR },
                },
            ],
        },
        MuiChip: {
            variants: [
                {
                    props: { variant: 'outlined', color: 'primary' },
                    style: {
                        border: `1px solid ${PRIMARY_COLOR}`,
                    },
                },
                {
                    props: { variant: 'outlined', color: 'secondary' },
                    style: {
                        border: `1px solid ${SECONDARY_COLOR}`,
                    },
                },
            ],
        },
        MuiSnackbarContent: {
            styleOverrides: {
                root: {
                    '&.MuiSnackbarContent-rootSuccess': {
                        backgroundColor: '#D7F2E7',
                        color: '#028A4F !important'
                    },
                    '&.MuiSnackbarContent-rootError': {
                        backgroundColor: '#FA3D3D', // TBC
                    },
                    '&.MuiSnackbarContent-rootInfo': {
                        backgroundColor: '#90B0D2', // TBC
                    },
                },
            },
        },
        MuiTextField: {
            defaultProps: {
                SelectProps: {
                    MenuProps: {
                        /**
                         * This fixes a default styling issue in MUI where TextField select
                         * dropdowns can get misaligned with their input fields on narrower
                         * screensizes if the dropdown is not at least 16px from the screen
                         * edges. Setting marginThreshold to 0 will prevent unnecessary
                         * edge-offsetting behaviour for TextFields dropdowns.
                         *
                         * @see {@link https://github.com/mui/material-ui/issues/35155}
                         */
                        marginThreshold: 0,
                    },
                },
            },
        },
    },
});

export const getTheme = (themeName: PinnacleThemes) =>
    createTheme(themes.get(themeName) || {});

export default getTheme;
